import React, { useRef, useState } from 'react';
import { Ellipsis, Pencil, Trash } from 'lucide-react';
import { Box, Card, Flex, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { Dropdown } from 'components/molecules/dropdown';
import { BoardEditColumnInput } from '../boardEditColumnInput';

export const BoardColumnHeader = ({
  headerRef,
  column,
  cards,
  cardsCount,
  hideColumnItemCount,
  onNewColumnName,
  onDeleteColumn,
  ...rest
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const dropdownRef = useRef(null);

  const onRename = (newValue, oldValue) =>
    newValue !== oldValue
      ? onNewColumnName({ id: column.status, label: newValue }, { id: column.status, label: oldValue }, column).then(
          () => {
            setIsEditing(false);
          },
        )
      : Promise.resolve();

  return (
    <Flex
      ref={headerRef}
      alignItems="center"
      height="32px"
      sx={{ cursor: column.id === 'column:unassigned' ? 'default' : 'grab' }}
      {...rest}
    >
      <>
        {isEditing ? (
          <BoardEditColumnInput value={column.title} onChange={onRename} onCancel={() => setIsEditing(false)} />
        ) : (
          <>
            <Text
              fontSize="14px"
              fontWeight="500"
              color="text.dark"
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {column.title}
              {cardsCount > 0 && !hideColumnItemCount && (
                <Text as="span" color="text.subtle">{` · ${cardsCount}`}</Text>
              )}
            </Text>
            {column.id !== 'column:unassigned' && (
              <Dropdown
                ref={dropdownRef}
                placement="bottom-end"
                trigger={
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    width="32px"
                    height="32px"
                    flex="0 0 auto"
                    sx={{
                      cursor: 'pointer',
                      color: 'neutral.400',
                    }}
                    aria-label="More actions"
                  >
                    <Box as={Ellipsis} width="16px" height="16px" aria-hidden="true" />
                  </Flex>
                }
              >
                <Card sx={{ width: '168px', py: '8px' }}>
                  <Flex
                    alignItems="center"
                    px="16px"
                    py="4px"
                    sx={{
                      gap: '8px',
                      color: 'text.main',
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'text.dark',
                      },
                    }}
                    onClick={() => {
                      dropdownRef.current.closeDropdown();
                      setIsEditing(true);
                    }}
                  >
                    <Box as={Pencil} size="16px" />
                    <Text fontSize="14px">Rename</Text>
                  </Flex>
                  <Box as="hr" variant="hr" />
                  <Flex
                    alignItems="center"
                    px="16px"
                    py="4px"
                    sx={{
                      gap: '8px',
                      color: 'red.600',
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'red.600',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      dropdownRef.current.closeDropdown();
                      onDeleteColumn(column, cards);
                    }}
                  >
                    <Box as={Trash} size="16px" />
                    <Text fontSize="14px">Delete</Text>
                  </Flex>
                </Card>
              </Dropdown>
            )}
          </>
        )}
      </>
    </Flex>
  );
};

BoardColumnHeader.propTypes = {
  headerRef: PropTypes.object.isRequired,
  column: PropTypes.object.isRequired,
  cardsCount: PropTypes.number,
  cards: PropTypes.array,
  hideColumnItemCount: PropTypes.bool,
  onNewColumnName: PropTypes.func.isRequired,
  onDeleteColumn: PropTypes.func.isRequired,
};

BoardColumnHeader.defaultProps = {
  cardsCount: 0,
  cards: [],
  hideColumnItemCount: false,
};
